import { useEffect, useState } from "react";
import { api, MARKETPLACES } from "../lib/api";
import { useActiveBling, blingLabel } from "../lib/blingContext";
import { toast } from "sonner";
import { Plus, Loader2, Trash2, Pencil } from "lucide-react";

const empty = {
  company_id: "", integration_id: "", marketplace: "",
  discount_type: "percent", value: 5, active: true,
};

export default function DiscountRules() {
  // Rules are per-Bling: the active store in the sidebar determines which
  // rules load and which ``integration_id`` is stamped on new rules. When
  // no Bling is selected we fall back to legacy account-wide rules.
  const { activeBling } = useActiveBling();
  const activeBlingId = activeBling?.id || "";
  const [list, setList] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const params = activeBlingId ? { integration_id: activeBlingId } : {};
      const [r, c] = await Promise.all([
        api.get("/discount-rules", { params }),
        api.get("/companies"),
      ]);
      setList(r.data);
      setCompanies(c.data);
    } catch (e) {
      toast.error("Erro ao carregar");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, [activeBlingId]);

  const save = async () => {
    try {
      const body = {
        ...form,
        marketplace: form.marketplace || null,
        // Stamp the current Bling on new rules so they're private to
        // this store from the moment they're created.
        integration_id: form.integration_id || activeBlingId || null,
      };
      if (form.id) await api.patch(`/discount-rules/${form.id}`, body);
      else await api.post("/discount-rules", body);
      toast.success("Regra salva");
      setForm(null);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Falha");
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Excluir regra?")) return;
    try {
      await api.delete(`/discount-rules/${id}`);
      toast.success("Excluída");
      load();
    } catch (e) { toast.error("Falha"); }
  };

  return (
    <div className="space-y-4" data-testid="discount-rules-page">
      <div className="flex items-end justify-between">
        <div>
          <div className="text-[10px] uppercase tracking-widest text-slate-500">Regras fiscais</div>
          <h1 className="text-3xl font-black tracking-tighter">Configuração de Descontos</h1>
          <p className="text-sm text-slate-600 mt-1">
            Aplicado automaticamente no envio ao ERP. Marketplace vazio = vale para todos.
            {activeBling ? (
              <span className="block mt-1 text-xs text-emerald-700 font-bold" data-testid="rules-scope-hint">
                Editando regras da loja: {blingLabel(activeBling)}
              </span>
            ) : null}
          </p>
        </div>
        <button data-testid="rule-new" onClick={() => setForm({ ...empty, company_id: companies[0]?.id || "" })} className="px-3 py-2 text-xs font-bold uppercase tracking-wider bg-[#002EA6] hover:bg-[#00207A] text-white flex items-center gap-2">
          <Plus className="w-4 h-4" /> Nova regra
        </button>
      </div>

      {form && (
        <div className="border border-slate-200 bg-white p-4 grid grid-cols-1 md:grid-cols-4 gap-3" data-testid="rule-form">
          <label className="block">
            <span className="block text-[10px] uppercase tracking-widest font-bold mb-1">Empresa</span>
            <select data-testid="rule-company" value={form.company_id} onChange={(e) => setForm({ ...form, company_id: e.target.value })} className="w-full border border-slate-300 px-2 py-2 text-sm">
              {companies.map((c) => <option key={c.id} value={c.id}>{c.nome_fantasia}</option>)}
            </select>
          </label>
          <label className="block">
            <span className="block text-[10px] uppercase tracking-widest font-bold mb-1">Marketplace</span>
            <select data-testid="rule-marketplace" value={form.marketplace || ""} onChange={(e) => setForm({ ...form, marketplace: e.target.value })} className="w-full border border-slate-300 px-2 py-2 text-sm">
              <option value="">(Todos)</option>
              {MARKETPLACES.map((m) => <option key={m.id} value={m.id}>{m.label}</option>)}
            </select>
          </label>
          <label className="block">
            <span className="block text-[10px] uppercase tracking-widest font-bold mb-1">Tipo</span>
            <select data-testid="rule-type" value={form.discount_type} onChange={(e) => setForm({ ...form, discount_type: e.target.value })} className="w-full border border-slate-300 px-2 py-2 text-sm">
              <option value="percent">Percentual (%)</option>
              <option value="fixed">Fixo (R$)</option>
            </select>
          </label>
          <label className="block">
            <span className="block text-[10px] uppercase tracking-widest font-bold mb-1">Valor</span>
            <input data-testid="rule-value" type="number" step="0.01" value={form.value} onChange={(e) => setForm({ ...form, value: parseFloat(e.target.value) || 0 })} className="w-full border border-slate-300 px-2 py-2 text-sm font-mono" />
          </label>
          <div className="md:col-span-4 flex justify-end gap-2">
            <button onClick={() => setForm(null)} className="px-3 py-2 text-xs font-bold uppercase border border-slate-300 hover:border-slate-900">Cancelar</button>
            <button data-testid="rule-save" onClick={save} className="px-4 py-2 text-xs font-bold uppercase bg-[#002EA6] hover:bg-[#00207A] text-white">Salvar</button>
          </div>
        </div>
      )}

      <div className="table-wrap">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 border-b border-slate-200 text-[10px] uppercase tracking-widest text-slate-600">
            <tr>
              <th className="text-left px-3 py-2.5">Empresa</th>
              <th className="text-left px-3 py-2.5">Marketplace</th>
              <th className="text-left px-3 py-2.5">Tipo</th>
              <th className="text-right px-3 py-2.5">Valor</th>
              <th className="text-right px-3 py-2.5">Ações</th>
            </tr>
          </thead>
          <tbody>
            {loading && (<tr><td colSpan="5" className="py-10 text-center"><Loader2 className="inline w-5 h-5 animate-spin" /></td></tr>)}
            {!loading && list.length === 0 && (<tr><td colSpan="5" className="py-10 text-center text-slate-500 text-sm">Nenhuma regra.</td></tr>)}
            {list.map((r) => {
              const co = companies.find((c) => c.id === r.company_id);
              return (
                <tr key={r.id} className="t-row border-b border-slate-100" data-testid={`rule-row-${r.id}`}>
                  <td className="px-3 py-2.5">{co?.nome_fantasia || "-"}</td>
                  <td className="px-3 py-2.5 text-xs">{r.marketplace || "Todos"}</td>
                  <td className="px-3 py-2.5 text-xs">{r.discount_type === "percent" ? "Percentual" : "Fixo"}</td>
                  <td className="px-3 py-2.5 text-right font-mono font-bold">{r.discount_type === "percent" ? `${r.value}%` : `R$ ${r.value.toFixed(2)}`}</td>
                  <td className="px-3 py-2.5 text-right">
                    <button data-testid={`rule-edit-${r.id}`} onClick={() => setForm(r)} className="p-1.5 hover:bg-slate-200 text-slate-600"><Pencil className="w-4 h-4" /></button>
                    <button data-testid={`rule-del-${r.id}`} onClick={() => remove(r.id)} className="p-1.5 hover:bg-red-100 text-red-600"><Trash2 className="w-4 h-4" /></button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}
